/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useRoleProfileSubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for handling role profile form submission.
────────────────────────────────────────────────────────────────*/

import { useState } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { createRoleProfile, updateRoleProfile } from '../api/roleProfilesApi';
import { RoleProfileFormData, RoleProfile } from '../types';

export function useRoleProfileSubmit(
  mode: 'create' | 'edit',
  id: string | undefined,
  formData: RoleProfileFormData,
  hasChanges: boolean,
  generateAllPermissions: () => Array<{ moduleSlug: string; level: 'FULL' | 'READ' | 'NONE' }>,
  setOriginalData: (data: RoleProfileFormData) => void,
) {
  const { user } = useAuthStore();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const tenantSlug = user?.tenant?.slug || t('profiles.defaultTenant');

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('profiles.validation.profileNameRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent): Promise<RoleProfile | undefined> => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // hasChanges validation removed

    if (!validateForm()) {
      showError(t('profiles.validation.formErrors'));
      return undefined;
    }

    try {
      setSaving(true);
      setErrors({});

      const allPermissions = generateAllPermissions();
      const completeFormData = {
        ...formData,
        permissions: allPermissions
      };

      let savedProfile;
      if (mode === 'create') {
        savedProfile = await createRoleProfile(completeFormData, tenantSlug, user?.id);
      } else if (id) {
        savedProfile = await updateRoleProfile(id, completeFormData, tenantSlug, user?.id);
      } else {
        throw new Error('Invalid form state');
      }

      showSuccess(mode === 'create'
        ? t('profiles.profileCreated')
        : t('profiles.profileUpdated')
      );

      setOriginalData({
        name: formData.name,
        description: formData.description,
        permissions: formData.permissions.map(p => ({ ...p }))
      });

      return savedProfile;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('profiles.errors.unknownError');
      showError(`${t('profiles.errors.errorSaving')}: ${errorMessage}`);
      return undefined;
    } finally {
      setSaving(false);
    }
  };

  return {
    saving,
    errors,
    setErrors,
    handleSubmit,
  };
}
